import { useState, FC, useEffect } from 'react';
import Image from 'next/legacy/image';
import {
  UiText,
  UiTitle,
  UiButton,
  UiIcon,
  notify,
} from '@frontend/znanie-uikit';
import { GraphQLError } from 'graphql-request/dist/types';

import { useUpdateUser } from 'hooks/user/useUpdateUser';
import { useRegistration } from 'hooks/become-speaker/useRegistration';

import { FormBySteps } from './components/FormByStep';

import {
  duplicatedEmailText,
  duplicatedPhoneText,
  duplicatedPhoneServerError,
  duplicatedEmailServerError,
} from 'utils/constants';
import { trackAnalyticsData } from 'utils/analytics';

import * as C from './Form.const';
import * as U from './Form.utils';
import * as T from './Form.types';
import * as H from './Form.hooks';

import BecomeSpeakerImage from './images/become-speaker.png';

import css from './Form.module.scss';

export const Form: FC<T.IFormProps> = (props) => {
  const [step, setStep] = useState(1);

  const { updateUser, updateUserLoading } = useUpdateUser();
  const { registerSpeaker, becomeSpeakerLoading } = useRegistration();

  useEffect(() => {
    window.scrollTo(0, 0);
    // При перемещении "назад" мы используем элемент, который не является
    // частью формы, соответственно, formik не обрабатывает ошибки.
    // Поэтому, явно вызываем валидацию
    formik.validateForm();
    formik.setStatus(C.FORMIK_STATUS.READY);
  }, [step]);

  const moveBackHandler = () => {
    const newStep = step - 1;

    if (newStep > 0) {
      setStep(newStep);
    }
  };

  const moveForwardHandler = () => {
    const newStep = step + 1;

    if (newStep <= C.STEPS_COUNT) {
      setStep(newStep);
    }
  };

  const submitFormikHandler = async () => {
    if (step < C.STEPS_COUNT) {
      moveForwardHandler();
      return;
    }

    try {
      await U.createSpeaker(
        props.user,
        formik.values,
        updateUser,
        registerSpeaker
      );
    } catch (err: any) {
      if (err.graphQLErrors.length > 0) {
        err.graphQLErrors.map(({ message }: GraphQLError) => {
          if (message === duplicatedPhoneServerError) {
            formik.errors.phone = duplicatedPhoneText;
          } else if (message === duplicatedEmailServerError) {
            formik.errors.email = duplicatedEmailText;
          }
        });
      } else {
        notify('Сервер временно недоступен. Повторите попытку позже.', {
          type: 'error',
        });
      }

      console.error(err.graphQLErrors);

      window.scrollTo(0, 0);
    }

    trackAnalyticsData(`become_speaker_${step}_step_complete`, {
      formFields: { ...formik.values },
    });
  };

  const formik = H.useBecomeSpeakerFormik(
    props.user,
    step,
    submitFormikHandler
  );

  const { values } = formik;

  const getSubmitBtnState = () => {
    if (formik.status === C.FORMIK_STATUS.INITIALIZING) {
      return true;
    }

    // если не последний шаг формы и есть ошибки валидации,
    // то дизейблим кнопку, иначе нет
    if (step !== C.STEPS_COUNT) {
      //валидация полей с файлами на 2 этапе формы
      if (step === 2) {
        if (
          values.hasFileCharterFilesError ||
          values.hasFileLettersOfHonorFilesError
        ) {
          return true;
        }
      }

      return !formik.isValid;
    }

    // если последний шаг формы, есть ошибки валидации
    // и чекбокс не чекнут, то дизейблим, иначе нет
    if (step === C.STEPS_COUNT) {
      return !formik.values.isConsent || !formik.isValid || formik.isSubmitting;
    }

    return false;
  };

  return (
    <div className={css.becomeSpeakerContainer}>
      <div className={css.formContainer}>
        <div className={css.titleWrapper}>
          <div className={css.controlBlock}>
            {step !== 1 && (
              <UiIcon
                name="back"
                width={17}
                height={15}
                onClick={moveBackHandler}
                className={css.backIcon}
              />
            )}

            <UiTitle className={css.title} size="big" as="h1">
              Стать лектором
            </UiTitle>
          </div>

          <UiText color="grey">
            {step}/{C.STEPS_COUNT}
          </UiText>
        </div>

        <form className={css.form} onSubmit={formik.handleSubmit}>
          <FormBySteps step={step} user={props.user} formik={formik} />

          <UiButton
            fluid
            type="submit"
            variant="primary"
            loading={becomeSpeakerLoading || updateUserLoading}
            tabIndex={100}
            disabled={getSubmitBtnState()}
            className={css.submitButton}
          >
            {step < C.STEPS_COUNT ? 'Далее' : 'Отправить заявку'}
          </UiButton>
        </form>
      </div>

      <div className={css.becomeSpeakerImage}>
        <Image
          src={BecomeSpeakerImage}
          layout="fill"
          alt="Изображение пользователей-лекторов"
          objectFit="contain"
          objectPosition="top"
          quality="75"
          priority
        />
      </div>
    </div>
  );
};
